import { z } from 'zod';
import { exportEncodingSchema, exportFormatSchema } from './enums.js';
/**
 * Core fields available as export columns alongside every attribute in the
 * catalogue. These are the values that live outside the field catalogue or need
 * a resolved representation of it.
 */
export const EXPORT_CORE_FIELDS = [
    /** Device-generated UUID v7 of the record. */
    'item_id',
    /** Server-assigned running number, e.g. AMB27-0142. */
    'short_code',
    'event_code',
    'event_name',
    /** Catalogue version the item was captured under. */
    'schema_version',
    /** Whether the item is flagged as needing follow-up. */
    'needs_review',
    /** Server-side timestamps, distinct from the device capture time. */
    'server_created_at',
    'updated_at',
    /** Comma-separated file names of the attached photos, useful with ZIP export. */
    'photo_filenames',
    'photo_count',
];
export const exportCoreFieldSchema = z.enum(EXPORT_CORE_FIELDS);
/** How a `user`-typed value is rendered. */
export const userRenderingSchema = z.enum(['name', 'email']);
/** How a value-list reference is rendered. */
export const valueListRenderingSchema = z.enum(['label', 'value', 'metadata']);
export const columnTransformSchema = z.object({
    /**
     * Date and datetime formatting pattern, e.g. `dd.MM.yyyy` or `yyyy-MM-dd`.
     * Null keeps the ISO representation.
     */
    dateFormat: z.string().max(50).nullable().default(null),
    /** Decimal separator for number, decimal and integer columns. */
    decimalSeparator: z.enum(['.', ',']).default('.'),
    /** Thousands separator. Empty string means none. */
    thousandsSeparator: z.enum(['', '.', ',', ' ', "'"]).default(''),
    prefix: z.string().max(50).default(''),
    suffix: z.string().max(50).default(''),
    /** Substituted whenever the value is null, undefined or an empty string. */
    emptyValue: z.string().max(100).default(''),
    caseTransform: z.enum(['none', 'upper', 'lower']).default('none'),
    /** How multi-valued fields (multiselect, photo) are joined into one cell. */
    multiValueSeparator: z.string().max(10).default('; '),
    /** Rendering of value-list references. `metadata` needs metadataKey. */
    valueListRendering: valueListRenderingSchema.default('label'),
    metadataKey: z.string().max(64).nullable().default(null),
    /** Rendering of user references. */
    userRendering: userRenderingSchema.default('name'),
    /** Rendering of booleans, as an exact pair of strings. */
    booleanTrue: z.string().max(50).default('true'),
    booleanFalse: z.string().max(50).default('false'),
});
export const exportColumnSchema = z
    .object({
    /** Where the value comes from: a core field or an attribute key. */
    source: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('core'), field: exportCoreFieldSchema }),
        z.object({ kind: z.literal('attribute'), key: z.string().min(1).max(64) }),
    ]),
    /** Free-form column heading. Defaults to the field label when omitted. */
    header: z.string().min(1).max(200),
    transform: columnTransformSchema,
})
    .refine((col) => col.transform.valueListRendering !== 'metadata' || col.transform.metadataKey !== null, {
    message: 'metadataKey is required when rendering a value list as metadata',
    path: ['transform', 'metadataKey'],
});
export const exportProfileSchema = z.object({
    id: z.uuid(),
    name: z.string().min(1).max(200),
    description: z.string().max(1000).nullable(),
    format: exportFormatSchema,
    /** Column order in the output is the array order. */
    columns: z.array(exportColumnSchema).min(1),
    // --- CSV dialect ---------------------------------------------------------
    delimiter: z.enum([',', ';', '\t', '|']).default(';'),
    quoteChar: z.enum(['"', "'"]).default('"'),
    /** always: quote every field. minimal: only when the value needs it. */
    quoting: z.enum(['always', 'minimal']).default('minimal'),
    lineEnding: z.enum(['\n', '\r\n']).default('\r\n'),
    encoding: exportEncodingSchema.default('utf-8'),
    includeHeader: z.boolean().default(true),
    /** ZIP output bundles the photo files alongside the data file. */
    includePhotos: z.boolean().default(false),
    createdByUserId: z.uuid().nullable(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable(),
});
/** Which records an export run covers. */
export const exportFilterSchema = z.object({
    eventIds: z.array(z.uuid()).default([]),
    supplierItemIds: z.array(z.uuid()).default([]),
    createdByUserIds: z.array(z.uuid()).default([]),
    capturedFrom: z.iso.datetime({ offset: true }).nullable().default(null),
    capturedTo: z.iso.datetime({ offset: true }).nullable().default(null),
    /** Include items flagged as needing follow-up. */
    includeNeedsReview: z.boolean().default(true),
});
//# sourceMappingURL=export-profile.js.map